import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, MessageSquare, User, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { value: 'Movies', label: 'Movies', color: 'bg-primary' },
  { value: 'Web Series', label: 'Web Series', color: 'bg-secondary' },
  { value: 'Theories', label: 'Theories', color: 'bg-purple-500' },
  { value: 'Recommendations', label: 'Recommendations', color: 'bg-green-500' },
  { value: 'General Discussion', label: 'General', color: 'bg-muted' }
];

export default function Community() {
  const [threads, setThreads] = useState([]);
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadThreads();
  }, [sort]);

  const loadThreads = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/community/threads?sort=${sort}`);
      setThreads(res.data);
    } catch (error) {
      console.error('Error loading threads:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center pt-20"><div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen pt-20" data-testid="community-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-2">Community</h1>
            <p className="text-muted-foreground">Join the conversation</p>
          </div>
          <Link to="/community/new" className="bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold btn-glow hover:opacity-90 transition-all active:scale-95 flex items-center space-x-2" data-testid="create-thread-button"><Plus className="w-5 h-5" /><span>New Thread</span></Link>
        </div>

        <div className="flex space-x-4 mb-8">
          <button onClick={() => setSort('newest')} className={`px-4 py-2 rounded-full font-medium transition-all ${sort === 'newest' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-accent'}`} data-testid="sort-newest">Newest</button>
          <button onClick={() => setSort('popular')} className={`px-4 py-2 rounded-full font-medium transition-all ${sort === 'popular' ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-accent'}`} data-testid="sort-popular">Most Commented</button>
        </div>

        {threads.length === 0 ? (
          <div className="text-center py-20"><MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-xl text-muted-foreground">No threads yet. Be the first to start a discussion!</p></div>
        ) : (
          <div className="space-y-4">
            {threads.map(thread => {
              const cat = CATEGORIES.find(c => c.value === thread.category);
              return (
                <Link key={thread.thread_id} to={`/community/${thread.thread_id}`} className="block bg-card border border-border rounded-lg p-6 hover:border-primary transition-colors" data-testid={`thread-${thread.thread_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`${cat?.color || 'bg-muted'} text-xs px-3 py-1 rounded-full text-white font-medium`}>{thread.category}</span>
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground"><User className="w-4 h-4" /><span>{thread.user_name}</span></div>
                        <div className="flex items-center space-x-1 text-sm text-muted-foreground"><Calendar className="w-4 h-4" /><span>{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span></div>
                      </div>
                      <h3 className="font-heading text-xl font-bold mb-2">{thread.title}</h3>
                      {thread.content && <p className="text-muted-foreground line-clamp-2">{thread.content}</p>}
                    </div>
                    <div className="flex items-center space-x-2 text-muted-foreground ml-4"><MessageSquare className="w-5 h-5" /><span className="font-semibold">{thread.comment_count}</span></div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}